import { useRef, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { HelpCenterTranslations } from '@/HelpCenter/ui/helpCenterTranslations'
import { useHelpCenter } from '@/HelpCenter/ui/context/HelpCenterContext'
import { useUserData } from '@/User/ui/context'
import { handleErrors } from '@/Shared/ui/form'
import {
    OreCardContainer,
    OreCardWrapper,
    OreCheckbox,
    OreFileUpload,
    OreInput,
    OreStack,
    OreSwitch,
    OreText,
    OreTextArea,
    SvgNavBarNotificactions,
    UploadedFile
} from '@edenredespana/oreneta'
import { navigationRoutes } from '@/Shared/ui/routers'
import { useNavigate } from 'react-router-dom'
import { useDownloaderHook } from '@/Shared/ui/downloader'
import { LegalDocumentType } from '@/Shared/domain'
import { AcceptConditionsOptions } from 'src/Account/domain/navbarModel'
import { navigationTranslation } from '@/Shared/ui/navigation/navigationTranslation'

export const NewTicketStep4 = (): JSX.Element => {
    const [displayAlternativeContact, setDisplayAlternativeContact] = useState(false)
    const [currentFilesSize, setCurrentFilesSize] = useState(0)
    const { downloadPublicDocument } = useDownloaderHook()

    const { t } = useTranslation()
    const { form, setHasFileError, uploadedFiles, setUploadedFiles } = useHelpCenter()
    const { user } = useUserData()
    const navigate = useNavigate()
    const { errors } = handleErrors(form.formState, form.getFieldState)

    const handleMyAccountClick = () => {
        navigate(navigationRoutes.configuration)
    }

    const options: AcceptConditionsOptions[] = [
        {
            name: t(navigationTranslation.header.user.privacyPolicy),
            documentId: LegalDocumentType.privacyPolicies
        },

    ]

    const handleClickConditions = (option: AcceptConditionsOptions) => {
        const { documentId, name } = option
        downloadPublicDocument(documentId, name)
    }

    const handleFilesChange = (files: UploadedFile[]) => {
        const totalSize = files.reduce((sum, f) => sum + f.size, 0)
        setCurrentFilesSize(totalSize)
        if (files.length !== uploadedFiles.length) {
            hasSizeOverflowRef.current = false
        }
        setHasFileError(totalSize > MAX_TOTAL_SIZE || hasSizeOverflowRef.current)
        setUploadedFiles(files)
        const convertFiles = async () => {
            const caseFiles = await Promise.all(
                files.map(async (uploadedFile) => {
                    const base64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader()
                        reader.onloadend = () => {
                            const result = reader.result as string
                            resolve(result.split(',')[1])
                        }
                        reader.readAsDataURL(uploadedFile.file)
                    })
                    return { name: uploadedFile.name, file: base64 }
                })
            )
            form.setValue('caseFiles', caseFiles)
        }
        convertFiles()
    }

    const MAX_TOTAL_SIZE = 6 * 1024 * 1024
    const ACCEPTED_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.xls', '.xlsx', '.docx', '.pdf'];
    const hasSizeOverflowRef = useRef(false)

    const handleFileInputCapture = (e: React.FormEvent<HTMLDivElement>) => {
        const target = e.target as HTMLInputElement
        if (target.type !== 'file' || !target.files || target.files.length === 0) return

        let newTotal = currentFilesSize
        for (let i = 0; i < target.files.length; i++) {
            newTotal += target.files[i].size
        }
        hasSizeOverflowRef.current = newTotal > MAX_TOTAL_SIZE
        if (newTotal > MAX_TOTAL_SIZE) {
            setHasFileError(true)
        }
    }

    return (
        <OreStack space="medium" placeContent="stretch">
            <OreStack space="xsmall" placeContent="stretch">
                <OreText size="text-lg" bold>{t(HelpCenterTranslations.step4.title)}</OreText>
                <OreText>{t(HelpCenterTranslations.step4.subtitle)}</OreText>
            </OreStack>
            <OreStack space="small" placeContent="stretch">
                <OreInput
                    {...form.register('subject')}
                    {...errors('subject')}
                    id="subject"
                    label={t(HelpCenterTranslations.step4.subject.label)}
                    placeholder={t(HelpCenterTranslations.step4.subject.placeholder)}
                    legend={t(HelpCenterTranslations.step4.subject.legend)}
                    maxLength={100}
                />
                <OreTextArea
                    {...form.register('description')}
                    {...errors('description')}
                    id="description"
                    label={t(HelpCenterTranslations.step4.description.label)}
                    placeholder={t(HelpCenterTranslations.step4.description.placeholder)}
                    legend={t(HelpCenterTranslations.step4.description.legend)}
                    maxLength={1000}
                    rows={5}
                />
                <div onChangeCapture={handleFileInputCapture}>
                    <OreFileUpload
                        buttonText={t(HelpCenterTranslations.step4.caseFiles.label)}
                        onFilesChange={handleFilesChange}
                        files={uploadedFiles}
                        fileTypeError={t(HelpCenterTranslations.step4.form.invalidFileType)}
                        maxTotalSize={MAX_TOTAL_SIZE}
                        totalSizeError={
                            <Trans
                                t={t}
                                i18nKey={HelpCenterTranslations.step4.form.maxFileSize}
                                components={[
                                    <a href="mailto:partners@es.edenred.info" />
                                ]}
                            />
                        }
                        acceptedFormats={ACCEPTED_EXTENSIONS}
                        validationLegend={t(HelpCenterTranslations.step4.caseFiles.legend)}
                    />
                </div>
            </OreStack>
            <OreStack space="medium" placeContent="stretch">
                <OreStack space="2xsmall" placeContent="stretch">
                    <OreText size="text-md">{t(HelpCenterTranslations.step4.contactData.title)}</OreText>
                    <OreText size="text-sm">{t(HelpCenterTranslations.step4.contactData.description)}</OreText>
                </OreStack>
                <OreCardContainer>
                    <OreCardWrapper>
                        <OreStack space="medium" placeContent="stretch">
                            <OreStack direction="row" space="2xlarge" placeContent="normal">
                                <OreStack space="2xsmall">
                                    <OreText size="text-md">{t(HelpCenterTranslations.step4.contactData.email)}</OreText>
                                    <OreText size="text-md" bold>{user?.user?.email}</OreText>
                                </OreStack>
                                <OreStack space="2xsmall">
                                    <OreText size="text-md">{t(HelpCenterTranslations.step4.contactData.phone)}</OreText>
                                    <OreText size="text-md" bold>{user?.user?.phoneNumber ?? "-"}</OreText>
                                </OreStack>
                            </OreStack>
                            <OreStack direction="row" placeItems="center"
                                style={{
                                    marginBottom: '1.5rem',
                                    backgroundColor: "#F1F5F9",
                                    borderRadius: "8px",
                                    border: "1px solid #CBD5E1",
                                    padding: "1rem"
                                }}>
                                <OreStack
                                    direction="row"
                                    placeItems="center"
                                    style={{
                                        paddingRight: "8px"
                                    }}>
                                    <SvgNavBarNotificactions />
                                    <OreText tone="neutral-600" >
                                        <Trans t={t}>
                                            {t(HelpCenterTranslations.step4.contactData.infoMessage)}
                                        </Trans>
                                    </OreText>
                                </OreStack>
                            </OreStack>
                        </OreStack>
                    </OreCardWrapper>
                </OreCardContainer>
                <OreSwitch
                    label={
                        <OreText
                            tone='neutral-900'
                            size='text-md'>
                            {t(HelpCenterTranslations.step4.contactData.alternativeContact)}
                        </OreText>}
                    name="useAlternativeContact"
                    checked={displayAlternativeContact}
                    onChange={() => {
                        setDisplayAlternativeContact(!displayAlternativeContact)
                        if (displayAlternativeContact) {
                            form.setValue('otherEmail', '', { shouldValidate: true })
                            form.setValue('otherPhone', '', { shouldValidate: true })
                        }
                    }}
                />
                {displayAlternativeContact && (
                    <OreStack space="small" direction="row" placeContent="stretch">
                        <OreInput
                            {...form.register('otherEmail')}
                            {...errors('otherEmail')}
                            id="otherEmail"
                            type="email"
                            placeholder={t(HelpCenterTranslations.step4.contactData.alternativeEmailPlaceholder)}
                            label={t(HelpCenterTranslations.step4.contactData.alternativeEmail)}
                        />
                        <OreInput
                            {...form.register('otherPhone')}
                            {...errors('otherPhone')}
                            id="otherPhone"
                            type="tel"
                            placeholder={t(HelpCenterTranslations.step4.contactData.alternativePhonePlaceholder)}
                            label={t(HelpCenterTranslations.step4.contactData.alternativePhone)}
                            legend={t(HelpCenterTranslations.step4.contactData.alternativePhoneLegend)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                    e.preventDefault()
                                }
                            }}
                        />
                    </OreStack>
                )}
            </OreStack>
            <OreCheckbox
                {...form.register('acceptPrivacyPolicy')}
                {...errors('acceptPrivacyPolicy')}
                label={
                    <OreText
                        size='text-md'
                        tone='neutral-900'>
                        <Trans
                            t={t}
                            components={[
                                <a
                                    onClick={() => handleClickConditions(options[0])}
                                    style={{ cursor: 'pointer' }}
                                />,
                            ]}>
                            {t(HelpCenterTranslations.step4.privacyPolicy.label)}
                        </Trans>
                    </OreText>
                }
            />
        </OreStack>
    )
}


